import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";

/**
 * The whole editor while it opens.
 *
 * Opening does three round trips before anything can be drawn: the business,
 * the workspace, and then the draft. Each answered with one line of text at the
 * top left of the window, so the reader watched three sentences in a row and
 * then the entire two-column editor appeared at once.
 *
 * The column width is the reader's own, read from the same store the real split
 * uses, so the seam does not jump when the data lands.
 */
export const WorkspaceSkeleton = ({
  width,
  label = "Loading payroll rules",
}: {
  width: number;
  label?: string;
}) => (
  <div
    role="status"
    aria-label={`${label}…`}
    style={
      { "--split": `${width}px 1px minmax(0, 1fr)` } as React.CSSProperties
    }
    className={cn(
      "grid min-h-0 w-full flex-1 grid-cols-1 grid-rows-[minmax(0,1fr)_minmax(0,1fr)] bg-surface-raised",
      "@min-[741px]/editor:grid-cols-(--split) @min-[741px]/editor:grid-rows-1",
    )}
  >
    {/* The conversation rail: a few messages, then the composer pinned to the
        foot where the real one sits. */}
    <Stack
      gap={0}
      align="stretch"
      direction="column"
      aria-hidden
      className="flex min-h-0 flex-col justify-end gap-4 p-5"
    >
      <Skeleton className="h-3 w-48 opacity-40" />
      <Skeleton className="h-3 w-64 opacity-40" />
      <Skeleton className="ml-auto h-16 w-56 rounded-xl opacity-60" />
      <Skeleton className="h-3 w-60 opacity-60" />
      <Skeleton className="h-24 w-full rounded-xl" />
    </Stack>
    <div
      aria-hidden
      className="hidden w-px bg-line @min-[741px]/editor:block"
    />
    <Stack
      gap={0}
      align="stretch"
      direction="column"
      className="flex min-h-0 flex-col"
    >
      {/* The tab strip, on the white band it really sits on. */}
      <Stack
        gap={0}
        align="stretch"
        direction="row"
        aria-hidden
        className="flex h-10 items-center gap-6 bg-surface-raised px-3 pb-px"
      >
        <Skeleton className="h-3 w-12" />
        <Skeleton className="h-3 w-8" />
        <Skeleton className="h-3 w-14" />
        <Skeleton className="h-3 w-14" />
        <Skeleton className="ml-auto h-6 w-20 rounded-full" />
      </Stack>
      {/* The artifact area, as rows. Which pane lands here is not known yet,
          and all three are row-shaped, so this is the honest common shape. */}
      <div
        aria-hidden
        className="min-h-0 flex-1 overflow-hidden bg-surface-raised"
      >
        <Stack
          gap={0}
          align="stretch"
          direction="row"
          className="flex items-center gap-3 bg-neutral px-3 py-1.5"
        >
          <Skeleton className="h-3 w-28" />
          <Skeleton className="h-3 w-48" />
        </Stack>
        {Array.from({ length: 30 }, (_, row) => (
          <Stack
            gap={0}
            align="stretch"
            direction="row"
            key={row}
            className="flex items-center gap-3 border-b border-line px-3 py-2"
          >
            {["w-72", "w-24", "w-16", "w-40"].map((width) => (
              <Skeleton key={width} className={cn("h-3 shrink-0", width)} />
            ))}
          </Stack>
        ))}
      </div>
    </Stack>
  </div>
);
